import React, { useState } from 'react';
import StageAvatar from './StageAvatar';
import ScreenShareView from './ScreenShareView';
import CandidateTile from './CandidateTile';

/**
 * The meeting "stage". Layout depends on whether the candidate is sharing their screen:
 *
 *   • NOT sharing       → Wiza's avatar fills the stage; candidate self-view is a PiP, top-right.
 *   • sharing (default) → Wiza + candidate sit side-by-side up top (both clearly visible); the shared
 *                         screen rides along as a SECONDARY strip below. Screen is recorded for
 *                         proctoring regardless — this just keeps the people in focus.
 *   • sharing + pinned  → the shared screen becomes the hero (full view); Wiza + candidate shrink to
 *                         PiPs. Toggled with the "Pin / Unpin" button.
 *
 * The avatar (StageAvatar) scales to fit whatever slot it's in (FitBox), so it never gets cut off.
 */
const WizaLabel = ({ className = '' }) => (
  <span className={`flex items-center gap-1.5 rounded-lg bg-black/55 px-2.5 py-1.5 text-xs font-medium text-white backdrop-blur ${className}`}>
    <span className="grid h-5 w-5 place-items-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-[10px] font-semibold">W</span>
    Wiza · AI Interviewer
  </span>
);

const PinIcon = ({ filled = false }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 4h6l-1 7 3 2v2H7v-2l3-2-1-7Z" /><path d="M12 15v5" />
  </svg>
);

const Shell = ({ children }) => (
  <div className="relative h-full w-full overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-100 dark:border-white/5 dark:from-slate-900 dark:to-slate-950">
    {children}
  </div>
);

// Wiza in a small PiP tile (pinned mode) — orb only, scaled to fit, with a label.
const WizaPip = ({ aiState, level, stateLabel }) => (
  <div className="relative h-[100px] w-[150px] overflow-hidden rounded-xl shadow-lg ring-1 ring-black/10 dark:ring-white/10 sm:h-[120px] sm:w-[180px]">
    <StageAvatar aiState={aiState} level={level} stateLabel={stateLabel} compact />
    <span className="absolute bottom-1.5 left-1.5 z-10 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">Wiza · AI</span>
  </div>
);

const AvatarStage = ({
  aiState = 'idle', level = 0, stateLabel,
  sharing = false, screenStream = null, hideMirror = true,
  camStream = null, cameraOn = false, muted = false,
}) => {
  const [pinned, setPinned] = useState(false);

  // ── not sharing: Wiza fills the stage, candidate self-view PiP ──
  if (!sharing) {
    return (
      <Shell>
        <StageAvatar aiState={aiState} level={level} stateLabel={stateLabel} />
        <WizaLabel className="absolute bottom-3 left-3 z-10" />
        <div className="absolute right-3 top-3 z-20 h-[104px] w-[164px] sm:h-[132px] sm:w-[208px]">
          <CandidateTile stream={camStream} cameraOn={cameraOn} muted={muted} className="h-full w-full" />
        </div>
      </Shell>
    );
  }

  // ── sharing + pinned: shared screen is the hero, people are PiPs ──
  if (pinned) {
    return (
      <Shell>
        <div className="absolute inset-0 p-2 sm:p-3">
          <ScreenShareView stream={screenStream} hideMirror={hideMirror} />
        </div>
        <div className="absolute right-3 top-3 z-20 flex gap-2">
          <WizaPip aiState={aiState} level={level} stateLabel={stateLabel} />
          <div className="h-[100px] w-[150px] sm:h-[120px] sm:w-[180px]">
            <CandidateTile stream={camStream} cameraOn={cameraOn} muted={muted} className="h-full w-full" />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setPinned(false)}
          className="absolute left-3 top-3 z-20 flex items-center gap-1.5 rounded-lg bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition hover:bg-black/75"
        >
          <PinIcon filled /> Unpin screen
        </button>
      </Shell>
    );
  }

  // ── sharing (default): Wiza | candidate up top, shared screen as a secondary strip ──
  return (
    <Shell>
      <div className="flex h-full w-full flex-col gap-2.5 p-2.5 sm:gap-3 sm:p-3">
        {/* people row */}
        <div className="flex min-h-0 flex-1 gap-2.5 sm:gap-3">
          <div className="relative min-w-0 flex-1 overflow-hidden rounded-xl">
            <StageAvatar aiState={aiState} level={level} stateLabel={stateLabel} />
            <WizaLabel className="absolute bottom-2 left-2 z-10" />
          </div>
          <CandidateTile stream={camStream} cameraOn={cameraOn} muted={muted} className="min-w-0 flex-1" />
        </div>

        {/* shared-screen secondary strip → click to pin/view full */}
        <button
          type="button"
          onClick={() => setPinned(true)}
          title="Pin the shared screen to view it full"
          className="group relative h-[26%] min-h-[92px] shrink-0 overflow-hidden rounded-xl text-left focus:outline-none focus:ring-2 focus:ring-primary-500/50"
        >
          <ScreenShareView stream={screenStream} hideMirror={hideMirror} />
          <span className="absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-lg bg-black/60 px-2.5 py-1.5 text-xs font-medium text-white opacity-90 backdrop-blur transition group-hover:opacity-100">
            <PinIcon /> Pin to view full
          </span>
        </button>
      </div>
    </Shell>
  );
};

export default AvatarStage;
